import type { FC } from 'react';
import { useMemo } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button/clickable-div';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';

import { useElementSize } from '../../../hooks/use-element-size';

import { TimeLeftCountdown } from '../time-left-countdown';
import type { StepupOfferCardProps } from '../type';
import { OfferCardStatusEnum } from '../type';

import { groupOfferByType } from './helper';
import { OfferGridGeneric } from './offer-grid-generic';
import { StepupCard } from './stepup-card';

const enclosure = (value: number, min: number, max: number) => {
  return Math.min(Math.max(min, value), max);
};

export interface StepupOfferGridProps {
  offers: StepupOfferCardProps[];
  description: string;
  endDate: string;
  claimedItems: number;
  banner: string;
  bodyBackgroundImage: string;
  ProgressBar: React.FC<{ total: number; current: number }>;
  onCloseModal?: () => void;
}
export const StepupOfferGrid: FC<StepupOfferGridProps> = ({
  offers,
  description,
  endDate,
  claimedItems,
  banner,
  ProgressBar,
  onCloseModal,
  bodyBackgroundImage,
}) => {
  const offerLength = offers.length;
  const { filteredOffers, firstGroupIndex, nGroups, parentTransform } =
    useMemo(() => {
      const groups = groupOfferByType(offers);
      let firstAvailableGroupIndex = enclosure(
        groups.findIndex((group) =>
          group.children.some(
            (item) => item.status !== OfferCardStatusEnum.CLAIMED
          )
        ),
        -1,
        Math.max(-1, groups.length - 1)
      );

      if (firstAvailableGroupIndex === -1) {
        firstAvailableGroupIndex = groups.length;
      }

      const _firstGroupIndex = Math.max(0, firstAvailableGroupIndex - 1);
      const lastGroupIndex = Math.min(groups.length - 1, _firstGroupIndex + 3);
      const firstOffer = groups[_firstGroupIndex]?.children[0];
      const lastGroup = groups[lastGroupIndex];
      const lastOffer = lastGroup?.children[lastGroup.children.length - 1];

      const filteredOffersResult = offers.filter(
        (offer) =>
          firstOffer &&
          offer.orderId >= firstOffer.orderId &&
          lastOffer &&
          offer.orderId <= lastOffer.orderId
      );

      return {
        nGroups: groups.length,
        filteredOffers: filteredOffersResult,
        firstGroupIndex: _firstGroupIndex,
        parentTransform: `translate(0,calc(${-firstAvailableGroupIndex * 100}%/${groups.length}))`,
      };
    }, [offers]);

  const [elementRef, { width }] = useElementSize();
  const gridRowHeight = (166 * (width - 40)) / 348;

  const gridHeight = gridRowHeight * 3 + 30;

  return (
    <div
      className="flex flex-col overflow-hidden rounded-[12px] border-[1.5px] border-solid border-[#FFA800]"
      ref={elementRef}
    >
      <div className="relative h-[173px]">
        <div className="absolute inset-x-0 top-0 h-[173px]">
          <div className="absolute inset-0">
            <img
              alt="step up banner"
              className="h-full w-full object-cover"
              src={banner}
            />
          </div>
          <div className="absolute inset-x-0 bottom-0 top-[67%] bg-[linear-gradient(180deg,rgba(21,25,52,0)_6.97%,#090A0C_61.83%)]" />
          <div className="absolute inset-x-0 bottom-[6px] flex flex-col items-stretch">
            <div className="flex w-full flex-row items-center justify-center text-xs text-white">
              <TimeLeftCountdown endDate={endDate} iconSize={21} />
            </div>
            <div className="flex h-[calc(3000%/180)] items-center justify-center">
              <span className="w-[250px] text-center text-xs uppercase text-white">
                {description}
              </span>
            </div>
          </div>
          <div className="font-poppins absolute right-0 top-0 text-sm text-white">
            <ClickableDiv
              className="cursor-pointer p-[9px] transition-transform hover:scale-125 active:scale-75"
              onClick={onCloseModal}
            >
              <IcoMoonIcon className="size-4" iconName="close" />
            </ClickableDiv>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-[-35px] h-[35px] bg-[linear-gradient(180deg,#0C0A08_35.41%,rgba(8,8,11,0)_89.8%)]" />
        <div className="absolute inset-x-0 bottom-[-35px] mx-5 my-[10px]">
          <ProgressBar current={claimedItems} total={offerLength} />
        </div>
      </div>

      <div
        className="bg-si pb-5 pt-[30px]"
        style={{
          backgroundImage: `url(${bodyBackgroundImage})`,
        }}
      >
        <div
          className="relative overflow-y-clip overflow-x-visible rounded-[12px] px-5"
          style={{ height: gridHeight }}
        >
          <div
            className="relative duration-500 ease-linear"
            style={{
              transform: parentTransform,
              height: nGroups * (gridRowHeight + 12),
            }}
          >
            <div
              className="absolute left-0 right-0"
              style={{
                top: firstGroupIndex * gridRowHeight + firstGroupIndex * 12 + 6,
              }}
            >
              <OfferGridGeneric
                CardComponent={StepupCard}
                className="3xl:grid-cols-6 xl:grid-cols-6"
                getKey={(offer) => offer.id}
                offers={filteredOffers}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
